import { FunctionComponent, ReactElement, useMemo } from 'react';
import useTitleBarSelect from '../../../../../hooks/useTitleBarSelect';
import CounterpartySummaryTable from './SummaryTable';
import CounterpartyBarChart from './BarChart';
import UltraTable, {
  TableData,
} from '../../../../tables/ultraTable/UltraTable';
import { ultraTableStyles } from '../../../../tables/ultraTable/styles/ultratable.styles';
import { buildColumns } from '../../../../tables/ultraTable/utils/buildUltraTableData';
import { COUNTERPARTY_COLUMNS } from '../data/counterpartyColumns';
import { buildCounterpartyTableData } from '../utils/buildCounterpartyTableData';
import CounterpartyDetailTable from './CounterpartyDetailTable';
import { Position } from '../../../../tables/ultraTable/types/position.types';
import { EditableFields } from '../../../../tables/ultraTable/types/editable.types';
import { formatDateForCheckingState } from '../../../../../utilities/dateFormatters';
import { makeStyles } from '@mui/styles';
import { RaptorTheme } from '../../../../../styling/theme';
import { QueryBoundary } from '../../../../utilityComponents/QueryBoundary';
import {
  RelevantCounterpartyPageModel,
  useRelevantCounterpartyData,
} from '../queries';

interface ICounterpartyTablesProps {
  selectedFund: string;
  positionDate: string | null;
  counterpartyOptions: any;
  fundName?: string;
}

export interface CounterpartyPositionEditable
  extends Position,
    EditableFields {}

const useCounterpartyTableStyles = makeStyles<RaptorTheme>(() => ({
  tableContainer: {
    height: 'auto',
  },
}));

function CounterpartyTables({
  selectedFund,
  counterpartyOptions,
  positionDate,
  fundName,
}: ICounterpartyTablesProps): ReactElement {
  const counterparty = useTitleBarSelect('counterpartySelection');

  const {
    data: relevantCounterpartyData,
    isLoading,
    isFetching,
    error,
  } = useRelevantCounterpartyData(
    selectedFund,
    counterparty,
    positionDate || undefined,
  );

  const currentCounterpartyName = counterpartyOptions.find(
    (el: any) => el.value === counterparty,
  )?.label;

  return (
    <QueryBoundary<RelevantCounterpartyPageModel>
      isLoading={isLoading}
      isFetching={isFetching}
      isError={!!error}
      data={relevantCounterpartyData?.data ? relevantCounterpartyData : null}
      loadingMessages={[
        'Loading Counterparty Details...',
        'This may take a moment...',
      ]}
      errorMessage="No data available for this counterparty"
    >
      {(pageModel) => (
        <>
          <CounterpartySummaryTable
            currentlySelectedCounterparty={currentCounterpartyName}
            selectedCounterpartyData={pageModel.data}
          />
          <CounterpartyBarChart inputData={pageModel.data as any} />
          <CounterpartyMainTableWrapper
            selectedCounterpartyData={pageModel.data}
            fundId={selectedFund}
            fundName={fundName || selectedFund}
            positionDate={positionDate}
            isFetching={isFetching}
          />
        </>
      )}
    </QueryBoundary>
  );
}

interface CounterpartyMainTableWrapperProps {
  selectedCounterpartyData: any;
  fundId: string;
  fundName: string;
  positionDate: string | null;
  isFetching?: boolean;
}

const CounterpartyMainTableWrapper: FunctionComponent<
  CounterpartyMainTableWrapperProps
> = ({ selectedCounterpartyData, fundId, fundName, positionDate, isFetching }) => {
  const classes = ultraTableStyles();
  const counterpartyClasses = useCounterpartyTableStyles();

  const data = useMemo(
    () => buildCounterpartyTableData(selectedCounterpartyData),
    [selectedCounterpartyData],
  );

  const columns = buildColumns(true, COUNTERPARTY_COLUMNS, classes);

  // Gotta fix the editable fields linter issues sooner or later
  const tableData: TableData<any> = {
    columns,
    data,
  };

  return (
    <UltraTable<CounterpartyPositionEditable>
      title="Counterparty Data"
      tableData={tableData}
      isFetching={isFetching}
      getRowCanExpand={(row) => {
        return !!(
          row.original.assetInformation &&
          Array.isArray(row.original.assetInformation) &&
          row.original.assetInformation.length > 0
        );
      }}
      pdfConfig={{
        pdfIdentifier: `${fundId}_counterparty_main`,
        pdfTitle: `Counterparty Data - ${fundName}`,
        pdfExportGroupName: 'counterparty_page',
        pdfExportGroupOrder: 5,
        pdfFileName: `counterparty-${fundId}-${
          positionDate || formatDateForCheckingState(new Date())
        }`,
      }}
      columnSelector
      positionDate={positionDate || undefined}
      nestedComponent={CounterpartyDetailTable}
      tableContainerClassName={counterpartyClasses.tableContainer}
    />
  );
};

export default CounterpartyTables;
/*

 */
